import { useEffect, useMemo, useRef, useState } from "react";
import { useClientTypeFilter } from "@/lib/ClientTypeFilterContext";

export function ClientTypeFilterDropdown() {
  const {
    selectedClientTypes,
    setSelectedClientTypes,
    availableClientTypes,
    loading,
  } = useClientTypeFilter();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const effectiveSelected = useMemo(
    () => selectedClientTypes ?? availableClientTypes,
    [selectedClientTypes, availableClientTypes]
  );

  const totalOptions = availableClientTypes.length;
  const selectedCount = effectiveSelected.length;
  const allSelected = totalOptions > 0 && selectedCount === totalOptions;

  const toggleType = (type: string) => {
    const current = new Set(effectiveSelected);

    if (current.has(type)) {
      current.delete(type);
    } else {
      current.add(type);
    }

    const next = Array.from(current).sort((a, b) => a.localeCompare(b, "fr"));

    if (next.length === 0 || next.length === totalOptions) {
      setSelectedClientTypes(null);
      return;
    }

    setSelectedClientTypes(next);
  };

  const buttonLabel = () => {
    if (loading) return "Types client…";
    if (totalOptions === 0) return "Types client";
    if (selectedClientTypes === null || allSelected) return "Tous types";
    return `${selectedCount} type${selectedCount > 1 ? "s" : ""}`;
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        disabled={loading || totalOptions === 0}
        className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        title="Filtrer le Kanban par type de client"
      >
        {buttonLabel()}
      </button>

      {isOpen && totalOptions > 0 && (
        <div className="absolute left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-30">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Filtrer par type client</span>
            <button
              type="button"
              onClick={() => setSelectedClientTypes(null)}
              className="text-xs text-indigo-600 hover:text-indigo-800"
            >
              Tous
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {availableClientTypes.map((type) => {
              const checked = selectedClientTypes === null || effectiveSelected.includes(type);
              return (
                <label
                  key={type}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleType(type)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-800">{type}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
